import { getElectronBridge } from '../../utilities/platform';
import type { Screenshot, Selection } from './screenshot';
export type { Screenshot, Selection } from './screenshot';

function canvasFor(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  const scale = Math.min(1, 1600 / Math.max(width, height));
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  return canvas;
}
function result(canvas: HTMLCanvasElement): Screenshot {
  const uri = canvas.toDataURL('image/jpeg', 0.8);
  return { uri, base64: uri.split(',')[1], mime: 'image/jpeg', width: canvas.width, height: canvas.height };
}
function loadImage(uri: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not read the screenshot.'));
    image.src = uri;
  });
}
export async function captureFeedbackScreenshot(): Promise<Screenshot> {
  const bridge = getElectronBridge();
  if (bridge) {
    if (!bridge.captureFeedbackScreenshot) throw new Error('Update the desktop app to capture a screen, or upload a screenshot.');
    // Allow React to remove the form before capturing the application window.
    await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    const captured = await bridge.captureFeedbackScreenshot();
    if (!captured.success || !captured.uri) throw new Error(captured.message || 'Could not capture the screen.');
    const image = await loadImage(captured.uri);
    const canvas = canvasFor(image.width, image.height);
    canvas.getContext('2d')!.drawImage(image, 0, 0, canvas.width, canvas.height);
    return result(canvas);
  }
  if (!navigator.mediaDevices?.getDisplayMedia) throw new Error('Screen capture is unavailable in this browser. Upload a screenshot instead.');
  // Call directly from the button gesture, before any asynchronous work.
  const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
  const video = document.createElement('video');
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    video.muted = true;
    video.srcObject = stream;
    await Promise.race([
      new Promise<void>((resolve, reject) => {
        video.onloadeddata = () => resolve();
        video.onerror = () => reject(new Error('Could not capture the screen.'));
        void video.play().catch(reject);
      }),
      new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('Screen capture timed out. Try again.')), 10000); }),
    ]);
    await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    if (!video.videoWidth || !video.videoHeight) throw new Error('No screen was captured.');
    const canvas = canvasFor(video.videoWidth, video.videoHeight);
    canvas.getContext('2d')!.drawImage(video, 0, 0, canvas.width, canvas.height);
    return result(canvas);
  } finally {
    clearTimeout(timer);
    stream.getTracks().forEach(track => track.stop());
    video.pause();
    video.srcObject = null;
  }
}
export async function markScreenshot(screenshot: Screenshot, selection: Selection | null): Promise<Screenshot> {
  const image = await loadImage(screenshot.uri);
  const canvas = canvasFor(image.width, image.height);
  const context = canvas.getContext('2d')!;
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  if (selection) {
    context.strokeStyle = '#e02020';
    context.lineWidth = 4;
    context.strokeRect(selection.x * canvas.width, selection.y * canvas.height,
      selection.width * canvas.width, selection.height * canvas.height);
  }
  return result(canvas);
}
